<template>
  <div class="modal-backdrop" @click.self="handleBackdropClick">
    <div class="relative max-h-full overflow-auto bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu rounded-xl w-[64rem]">
      <div class="modal-header border-b-2 border-n-slate-4 dark:border-n-slate-7">
        <h3 class="modal-title">Editar Cadastro</h3>
        <button
          class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
          @click="closeModal">
          <span class="i-lucide-x flex-shrink-0"></span>
        </button>
      </div>

      <div class="p-6">
        <div class="flex gap-6 lg:flex-row flex-col">
          <!-- Left Column - Input Fields -->
          <div class="flex-1">
            <!-- CAMPOS OBRIGATÓRIOS PRIMEIRO -->
            <div class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Informações Básicas <span class="text-red-500">*</span>
              </h4>
              
              <!-- Product/Service Name -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Nome do item <span class="text-red-500">*</span></label>
                <input type="text" v-model="formData.name" placeholder="Insira o nome do item" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" required />
              </div>

              <!-- Product/Service Description -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Descrição do item <span class="text-red-500">*</span></label>
                <textarea 
                  v-model="formData.description" 
                  placeholder="Insira a descrição do item"
                  class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 resize-y" 
                  rows="4"
                  maxlength="2048"
                ></textarea>
                <div class="text-xs mt-1" :class="{'text-red-500': descriptionCharsRemaining < 100, 'text-n-slate-10': descriptionCharsRemaining >= 100}">
                  {{ descriptionCharsRemaining }} caracteres restantes
                </div>
              </div>

              <!-- Product/Service Type -->
              <div v-if="!isService" class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tipo do produto</label>
                <div class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-base bg-n-slate-1 dark:bg-n-slate-8 text-n-slate-11 cursor-not-allowed">
                  {{ formData.productType || 'Não definido' }}
                </div>
              </div>

              <!-- Service Type -->
              <div v-if="isService" class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tipo de serviço</label>
                <div class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-base bg-n-slate-1 dark:bg-n-slate-8 text-n-slate-11 cursor-not-allowed">
                  {{ formatServiceType(formData.serviceType) || 'Não definido' }}
                </div>
              </div>

              <!-- Product Value with prefix "R$" -->
              <div v-if="!isService" class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Valor do produto <span class="text-red-500">*</span></label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="formattedValue" @input="handleValueInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" required />
                </div>
              </div>
            </div>

            <!-- CAMPOS OPCIONAIS -->
            <div v-if="!isService" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Informações Adicionais
              </h4>

              <!-- Discount Price with prefix "R$" -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Preço com desconto</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="formattedDiscountPrice" @input="handleDiscountPriceInput" @keydown="handleDiscountPriceKeydown" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Reason -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Condição para desconto</label>
                <input type="text" v-model="formData.discountReason" placeholder="Explique quando a IA deve oferecer o desconto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" />
              </div>

              <!-- Context URL -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">URL do item</label>
                <input type="url" v-model="formData.contextUrl" placeholder="https://exemplo.com/item" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="500" />
                <div class="text-xs text-n-slate-10 mt-1 leading-normal">
                  Seus agentes de IA poderão ler desse link ou enviá-lo para clientes
                </div>
              </div>
            </div>

            <!-- Campos específicos para produtos físicos -->
            <div v-if="formData.productType === 'Físico'" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Detalhes do Produto Físico
              </h4>

              <!-- Weight -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Peso (kg)</label>
                <input type="number" v-model="formData.weightKg" placeholder="0.000" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       step="0.001" min="0" />
              </div>

              <!-- Dimensions -->
              <div>
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Dimensões (cm)</label>
                <div class="grid grid-cols-3 gap-2 mb-5">
                  <div>
                    <input type="number" v-model="formData.dimensionsCm.height" placeholder="Altura" 
                           class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                           min="0" step="0.1" />
                    <div class="text-xs text-n-slate-10 mt-1">Altura</div>
                  </div>
                  <div>
                    <input type="number" v-model="formData.dimensionsCm.width" placeholder="Largura" 
                           class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                           min="0" step="0.1" />
                    <div class="text-xs text-n-slate-10 mt-1">Largura</div>
                  </div>
                  <div>
                    <input type="number" v-model="formData.dimensionsCm.depth" placeholder="Profundidade" 
                           class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                           min="0" step="0.1" />
                    <div class="text-xs text-n-slate-10 mt-1">Profundidade</div>
                  </div>
                </div>
              </div>

              <!-- Color -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Cores</label>
                <input type="text" v-model="formData.color" placeholder="Cores do produto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="50" />
              </div>

              <!-- Ships To -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Envia para</label>
                <input type="text" v-model="formData.shipsTo" placeholder="Brasil, São Paulo..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="200" />
              </div>
            </div>

            <!-- Campos específicos para produtos digitais -->
            <div v-if="formData.productType === 'Digital'" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Detalhes do Produto Digital
              </h4>

              <!-- Formato do arquivo -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Formato do arquivo</label>
                <input type="text" v-model="formData.fileFormat" placeholder="PDF, MP4, ZIP..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="100" />
              </div>

              <!-- Tipo de licença -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tipo de licença</label>
                <input type="text" v-model="formData.licenseType" placeholder="Licença vitalícia, mensal..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="100" />
              </div>
            </div>

            <!-- Service-specific fields -->
            <div v-if="isService" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Informações Adicionais do Serviço
              </h4>

              <!-- Service Link -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Link do serviço</label>
                <input type="url" v-model="formData.link" placeholder="https://exemplo.com/servico" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="500" />
                <div class="text-xs text-n-slate-10 mt-1 leading-normal">
                  Seus agentes de IA poderão ler desse link ou enviá-lo para clientes
                </div>
              </div>

              <!-- Service Address -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Endereço</label>
                <input type="text" v-model="formData.address" placeholder="Rua, número, bairro, cidade..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" />
              </div>
            </div>

            <!-- Service fields for delivery -->
            <div v-if="isService && formData.serviceType === 'delivery'" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Detalhes do Delivery
              </h4>

              <!-- Service Value -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Valor do serviço</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="formattedValue" @input="handleValueInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Price with prefix "R$" -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Preço com desconto</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="formattedDiscountPrice" @input="handleDiscountPriceInput" @keydown="handleDiscountPriceKeydown" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Reason -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Condição para desconto</label>
                <input type="text" v-model="formData.discountReason" placeholder="Explique quando a IA deve oferecer o desconto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" />
              </div>

              <!-- Max Distance -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Distância máxima</label>
                <input type="text" v-model="formData.maxDistance" placeholder="Birigui, Araçatuba, Coroados..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" />
              </div>

              <!-- Fixed Price Toggle -->
              <div class="mb-5">
                <label class="flex items-center cursor-pointer">
                  <input type="checkbox" v-model="formData.isFixedPrice" 
                         class="mr-3 w-4 h-4 text-n-brand bg-n-background border-n-slate-4 rounded focus:ring-n-brand dark:focus:ring-n-brand dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600" />
                  <span class="text-sm font-medium text-n-slate-12">Preço fixo?</span>
                </label>
              </div>

              <!-- Estimated Time -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tempo estimado (minutos)</label>
                <input type="number" v-model="formData.estimatedMinutes" placeholder="30" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       min="1" max="480" />
              </div>
            </div>

            <!-- Service fields for consulta, tratamento, aluguel -->
            <div v-if="isService && (formData.serviceType === 'consulta' || formData.serviceType === 'tratamento' || formData.serviceType === 'aluguel')" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Detalhes do {{ formData.serviceType === 'consulta' ? 'Consulta' : formData.serviceType === 'tratamento' ? 'Tratamento' : 'Aluguel' }}
              </h4>

              <!-- Hourly Rate Toggle -->
              <div class="mb-5">
                <label class="flex items-center cursor-pointer">
                  <input type="checkbox" v-model="formData.isHourlyRate" 
                         class="mr-3 w-4 h-4 text-n-brand bg-n-background border-n-slate-4 rounded focus:ring-n-brand dark:focus:ring-n-brand dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600" />
                  <span class="text-sm font-medium text-n-slate-12">Valor por hora?</span>
                </label>
              </div>

              <!-- Service Value -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">
                  {{ formData.isHourlyRate ? 'Valor por hora' : 'Valor do serviço' }}
                </label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="formattedValue" @input="handleValueInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Price with prefix "R$" -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Preço com desconto</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="formattedDiscountPrice" @input="handleDiscountPriceInput" @keydown="handleDiscountPriceKeydown" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Reason -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Condição para desconto</label>
                <input type="text" v-model="formData.discountReason" placeholder="Explique quando a IA deve oferecer o desconto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" />
              </div>


              <!-- Specialty field for Consulta and Tratamento -->
              <div v-if="formData.serviceType === 'consulta' || formData.serviceType === 'tratamento'" class="mb-2">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12" for="edit-service-specialty-select">Especialidade</label>
                <select id="edit-service-specialty-select" v-model="formData.specialty">
                  <option value="">Nenhuma selecionada</option>
                  <option v-for="specialty in specialties" :key="specialty.id" :value="specialty.id">
                    {{ specialty.name }}
                  </option>
                </select>
              </div>

              <!-- Duration field for Consulta and Tratamento -->
              <div v-if="formData.serviceType === 'consulta' || formData.serviceType === 'tratamento'" class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tempo de duração (minutos)</label>
                <input type="number" v-model="formData.durationMinutes" placeholder="60" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       min="1" />
                <div class="text-xs text-n-slate-10 mt-1 leading-normal">
                  Duração do atendimento em minutos. Padrão 60 minutos caso não especificado.
                </div>
              </div>

              <!-- Advance Booking Days -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Marcar com antecedência (dias)</label>
                <input type="number" v-model="formData.advanceBookingDays" placeholder="1" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       min="0" max="365" />
                <div class="text-xs text-n-slate-10 mt-1 leading-normal">
                  Quantos dias de antecedência são necessários para o agendamento
                </div>
              </div>
            </div>

            <!-- Service fields for assinatura -->
            <div v-if="isService && formData.serviceType === 'assinatura'" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Detalhes da Assinatura
              </h4>

              <!-- Service Value -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Preço do plano</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="formattedValue" @input="handleValueInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Price -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Preço de desconto</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="formattedDiscountPrice" @input="handleDiscountPriceInput" @keydown="handleDiscountPriceKeydown" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Reason -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Condição para desconto</label>
                <input type="text" v-model="formData.discountReason" placeholder="Explique quando a IA deve oferecer o desconto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" />
              </div>

              <!-- Plan Type -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tipo de plano</label>
                <input type="text" v-model="formData.planType" placeholder="Anual, mensal..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="100" />
              </div>
            </div>
          </div>
          
          <!-- Right Column - Sticky Sidebar -->
          <div class="w-full lg:w-80 flex-shrink-0">
            <div class="lg:sticky lg:top-0 pt-5">
              <!-- Inbox Selection -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Inboxes do item</label>
                <div class="relative inbox-dropdown">
                  <button
                    @click.stop="toggleInboxDropdown"
                    class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 flex justify-between items-center text-left"
                    type="button"
                  >
                    <div class="flex items-center overflow-hidden">
                      <div 
                        class="w-6 h-6 flex items-center justify-center rounded-full mr-2 flex-shrink-0"
                        :class="getSelectedInboxesDisplayClasses()"
                      >
                        <i :class="getSelectedInboxesDisplayIcon() + ' text-sm'"></i>
                      </div>

                      <span class="truncate">
                        {{ getSelectedInboxesDisplayText() }}
                      </span>
                    </div>
                    <i class="i-lucide-chevron-down ml-2 flex-shrink-0 transition-transform" :class="{ 'rotate-180': showInboxDropdown }"></i>
                  </button>
                  
                  <!-- Dropdown Options -->
                  <div v-if="showInboxDropdown" 
                       class="absolute z-50 top-full left-0 right-0 mt-1 border border-n-slate-3 rounded-md shadow-lg bg-white dark:bg-slate-800 overflow-hidden"
                       @click.stop>
                    <!-- Todos option -->
                    <div class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                         @click.stop="toggleInboxSelection('todos')">
                      <div class="flex items-center">
                        <input type="checkbox" 
                               :checked="isInboxSelected('todos')" 
                               @click.stop
                               @change.stop="toggleInboxSelection('todos')"
                               class="mr-3">
                        <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                          <i class="i-lucide-globe"></i>
                        </div>
                        <div>
                          <div class="text-sm font-medium">Todos</div>
                          <div class="text-xs text-slate-500">Disponível para todos os inboxes</div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Grouped Inboxes -->
                    <div class="max-h-60 overflow-y-auto">
                      <template v-for="(item, index) in groupedInboxOptions" :key="index">
                        <!-- Group Header -->
                        <div v-if="item.type === 'header'"
                             class="px-3 py-1 text-xs font-semibold bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-y border-slate-100 dark:border-slate-700 sticky top-0 z-10">
                          {{ item.label }}
                        </div>
                        
                        <!-- Inbox Option -->
                        <div v-else
                             class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                             @click.stop="toggleInboxSelection(String(item.inbox_id))">
                          <div class="flex items-center">
                            <input type="checkbox" 
                                   :checked="isInboxSelected(String(item.inbox_id))" 
                                   @click.stop
                                   @change.stop="toggleInboxSelection(String(item.inbox_id))"
                                   class="mr-3">
                            <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-grass-300 text-grass-900">
                              <i :class="getInboxTypeIcon(item.channel_type)"></i>
                            </div>
                            
                            <div>
                              <div class="text-sm font-medium">
                                {{ getInboxDisplayName(item) }}
                              </div>
                              <div v-if="item.integration_type === 'official'" class="text-xs text-grass-700 dark:text-grass-400">
                                API Oficial
                              </div>
                              <div v-else-if="item.integration_type === 'evolution'" class="text-xs text-grass-700 dark:text-grass-400">
                                Não Oficial
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="text-xs text-n-slate-10 mt-2 leading-normal">
                  Selecione os inboxes onde este item estará disponível para os agentes de IA
                </div>
              </div>

              <!-- Product/Service Category -->
              <IntegratedListView 
                v-model="formData.category" 
                title="Categoria do item"
                input-placeholder="Digite a categoria e pressione Enter" 
                empty-message="Nenhuma categoria adicionada"
                required 
                class="mb-6"
              />

              <!-- Product/Service Images -->
              <div>
                <label class="block mb-3 text-sm font-medium text-n-slate-12">Imagens do item</label>
                <!-- Error message for file upload -->
                <p v-if="error && (error.includes('Arquivo muito grande') || error.includes('imagem'))" class="text-red-500 text-sm mb-3">
                  {{ error }}
                </p>
                <button
                  type="button"
                  @click="triggerFileUpload"
                  :disabled="loading"
                  class="inline-flex items-center w-full gap-2 transition-all duration-200 ease-in-out border border-n-slate-4 dark:border-n-slate-7 rounded-lg outline-1 outline disabled:opacity-50 bg-n-background text-n-slate-12 hover:enabled:bg-n-slate-1 focus-visible:bg-n-slate-1 outline-transparent h-10 px-4 text-sm font-medium justify-center mb-3"
                >
                  <span v-if="loading" class="animate-spin i-lucide-loader flex-shrink-0"></span>
                  <span v-else class="i-lucide-upload flex-shrink-0"></span>
                  <span>{{ loading ? 'Enviando...' : 'Enviar' }}</span>
                </button>
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  multiple
                  @change="handleFileUpload"
                  class="hidden"
                />
              </div>
              <IntegratedImageView 
                v-model="formData.images" 
                title=""
                input-placeholder="Ou digite a URL da imagem" 
                empty-message="Nenhuma imagem adicionada"
                :modal-loading="loading"
              />
            </div>
          </div>
        </div>

        <p v-if="error && !error.includes('Arquivo muito grande') && !error.includes('imagem')" class="text-red-500 text-sm mt-4">{{ error }}</p>
      </div>

      <div class="flex justify-end gap-3 p-4 border-t-2 border-n-slate-4 dark:border-n-slate-7 sticky bottom-0 bg-n-alpha-3">
        <button type="button"
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
          @click="closeModal" :disabled="loading">
          Cancelar
        </button>
        <button type="button"
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
          @click="saveProduct" :disabled="!isFormValid || loading">
          <span v-if="loading" class="animate-spin i-lucide-loader"></span>
          <span>{{ loading ? 'Salvando...' : 'Salvar cadastro' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
import IntegratedListView from '@/dashboard/components/IntegratedListView.vue';
import IntegratedImageView from '@/dashboard/components/IntegratedImageView.vue';
import wootAPI from '@/v3/api/apiClient';

export default {
  name: 'EditProductModal',
  components: { IntegratedListView, IntegratedImageView },
  props: {
    product: {
      type: Object,
      required: true
    },
    connectedInboxes: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'update', 'success'],
  setup(props, { emit }) {
    const store = useStore();
    
    // Determine if we're editing a service or product
    const isService = computed(() => {
      return props.product.product_type === 'servico' || 
             (props.product.meta_data && typeof props.product.meta_data.type === 'string' && 
              ['aluguel', 'delivery', 'consulta', 'tratamento', 'assinatura'].includes(props.product.meta_data.type));
    });

    // Initialize form data with the product's current values
    const formData = ref({
      name: '',
      description: '',
      price: null,
      discountPrice: null,
      discountReason: '',
      contextUrl: '',
      productType: '',
      selectedInboxes: ['todos'],
      category: [],
      images: [],
      // Campos específicos para produtos físicos
      weightKg: null,
      dimensionsCm: {
        height: null,
        width: null,
        depth: null
      },
      color: '',
      shipsTo: '',
      // Campos específicos para produtos digitais
      fileFormat: '',
      licenseType: '',
      // Campos específicos para serviços
      serviceType: '',
      link: '',
      address: '',
      // Campos específicos para delivery
      maxDistance: '',
      isFixedPrice: false,
      estimatedMinutes: null,
      // Campos específicos para consulta, tratamento e aluguel
      isHourlyRate: false,
      advanceBookingDays: null,
      specialty: '',
      durationMinutes: null
    });

    const formattedValue = ref('');
    const formattedDiscountPrice = ref('');
    const loading = ref(false);
    const error = ref(null);
    const fileInput = ref(null);
    
    // Inbox selection state
    const inboxOptions = ref([]);
    const showInboxDropdown = ref(false);
    
    // Specialties for service form
    const specialties = ref([]);

    // Computador para mostrar caracteres restantes na descrição
    const descriptionCharsRemaining = computed(() => {
      const MAX_DESCRIPTION_LENGTH = 2048;
      return MAX_DESCRIPTION_LENGTH - (formData.value.description?.length || 0);
    });

    // Store the original selected inboxes to restore after loading
    const originalSelectedInboxes = ref([]);

    // Populate form with product data when component mounts
    onMounted(async () => {
      // Store the original selected inboxes, coercing to strings
      console.log('Product inbox_id:', props.product.inbox_id);
      
      // Handle empty array case - don't default to 'todos'
      const inboxIds = props.product.inbox_id;
      // If inbox_id is null, it means "todos" was selected
      originalSelectedInboxes.value = inboxIds === null 
        ? ['todos']
        : Array.isArray(inboxIds) && inboxIds.length === 0 
        ? [] 
        : (inboxIds || ['todos']).map(id => String(id));
      
      formData.value = {
        name: props.product.name || '',
        description: props.product.description || '',
        price: props.product.price !== null && props.product.price !== undefined ? parseFloat(props.product.price) : (props.product.meta_data?.price ?? 0),
        discountPrice: props.product.discount_price !== null && props.product.discount_price !== undefined ? parseFloat(props.product.discount_price) : null,
        discountReason: props.product.discount_reason || '',
        contextUrl: props.product.context_url || '',
        productType: props.product.meta_data?.type === 'physical' ? 'Físico' : props.product.meta_data?.type === 'digital' ? 'Digital' : '',
        selectedInboxes: inboxIds === null 
          ? ['todos']
          : Array.isArray(inboxIds) && inboxIds.length === 0 
          ? [] 
          : (inboxIds || ['todos']).map(id => String(id)),
        // Convert category to array of objects if it's not already
        category: Array.isArray(props.product.category)
          ? props.product.category.map(cat => typeof cat === 'string' ? { name: cat } : cat)
          : props.product.category ? [{ name: props.product.category }] : [],
        // Handle images_url from database - it's stored as an array of strings
        images: Array.isArray(props.product.images_url)
          ? props.product.images_url.map(url => ({ url: url }))
          : Array.isArray(props.product.images)
          ? props.product.images.map(img => typeof img === 'string' ? { url: img } : img)
          : props.product.images ? [{ url: props.product.images }] : [],
        // Physical product fields from meta_data
        weightKg: props.product.meta_data?.weightKg || null,
        dimensionsCm: {
          height: props.product.meta_data?.dimensionsCm ? props.product.meta_data.dimensionsCm[0] : null,
          width: props.product.meta_data?.dimensionsCm ? props.product.meta_data.dimensionsCm[1] : null,
          depth: props.product.meta_data?.dimensionsCm ? props.product.meta_data.dimensionsCm[2] : null
        },
        color: props.product.meta_data?.color || '',
        shipsTo: props.product.meta_data?.shipsTo || '',
        // Digital product fields from meta_data
        fileFormat: props.product.meta_data?.fileFormat || '',
        licenseType: props.product.meta_data?.licenseType || '',
        // Service-specific fields from meta_data
        serviceType: props.product.meta_data?.type || '',
        link: props.product.context_url || '',
        address: props.product.meta_data?.address || '',
        // Delivery fields
        maxDistance: props.product.meta_data?.maxDistance || '',
        isFixedPrice: props.product.meta_data?.isFixedPrice || false,
        estimatedMinutes: props.product.meta_data?.estimatedMinutes || null,
        // Appointment fields (consulta, tratamento, aluguel)
        isHourlyRate: props.product.meta_data?.isHourlyRate || false,
        advanceBookingDays: props.product.meta_data?.advanceBookingDays || null,
        specialty: props.product.meta_data?.specialty || '',
        durationMinutes: props.product.meta_data?.durationMinutes || null,
        // Assinatura fields
        planType: props.product.meta_data?.planType || ''
      };
      loadInboxes();  // Now synchronous, gets data from Vuex store
      await loadSpecialties();
    });

    const formatToBRL = (value) => {
      if (value === null || value === undefined) return '';
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2
      }).format(value);
    };

    const handleValueInput = (event) => {
      error.value = null;
      const input = event.target.value;
      const numeric = input.replace(/\D/g, '');
      const value = Number(numeric) / 100;

      formData.value.price = value;
      formattedValue.value = formatToBRL(value).replace('R$', '').trim();
    };

    const handleDiscountPriceInput = (event) => {
      error.value = null;
      const input = event.target.value.trim();
      
      // If input is completely empty, set to 0
      if (input === '') {
        formData.value.discountPrice = 0;
        return;
      }
      
      // Otherwise, process as number
      const numeric = input.replace(/\D/g, '');
      const value = Number(numeric) / 100;

      formData.value.discountPrice = value;
      formattedDiscountPrice.value = formatToBRL(value).replace('R$', '').trim();
    };

    // Handle key events to allow clearing the discount price field
    const handleDiscountPriceKeydown = (event) => {
      // If user presses Delete or Backspace and field would become empty, reset to 0
      if ((event.key === 'Delete' || event.key === 'Backspace')) {
        const input = event.target;
        const value = input.value;
        
        // If field is about to be cleared (selection covers all content or content is minimal)
        if (input.selectionStart === 0 && input.selectionEnd === value.length) {
          // User is selecting all and deleting - reset to 0
          event.preventDefault();
          formData.value.discountPrice = 0;
          return;
        }
        
        // If field only contains "0,00" and user is backspacing/deleting
        if (value === '0,00' || value === '0.00' || value === '0') {
          event.preventDefault();
          formData.value.discountPrice = 0;
          return;
        }
      }
    };

    watch(() => formData.value.price, (newVal) => {
      if (newVal !== null && newVal !== undefined) {
        formattedValue.value = formatToBRL(newVal).replace('R$', '').trim();
      }
    }, { immediate: true });

    watch(() => formData.value.discountPrice, (newVal) => {
      // Always format as currency, treating null/undefined as 0
      const valueToFormat = (newVal !== null && newVal !== undefined) ? newVal : 0;
      formattedDiscountPrice.value = formatToBRL(valueToFormat).replace('R$', '').trim();
    }, { immediate: true });

    // Monitore o comprimento da descrição para mostrar erro quando estiver no limite
    watch(() => formData.value.description, (newValue) => {
      if (newValue && newValue.length >= 2048) {
        error.value = "A descrição atingiu o limite máximo de 2048 caracteres.";
      } else {
        // Só limpa o erro se estiver relacionado à descrição
        if (error.value === "A descrição atingiu o limite máximo de 2048 caracteres.") {
          error.value = null;
        }
      }
    });

    // Clear error when user edits any field except description
    watch([
      () => formData.value.name,
      () => formData.value.category,
      () => formData.value.images
    ], () => { 
      // Limpa erros gerais quando o usuário edita um campo
      if (error.value !== "A descrição atingiu o limite máximo de 2048 caracteres.") {
        error.value = null;
      }
    });

    // Form validation: ensure all required fields have values
    const isFormValid = computed(() => {
      if (isService.value) {
        // Service validation
        const basicServiceValidation = (
          formData.value.name &&
          formData.value.description &&
          formData.value.description.length <= 2048 &&
          formData.value.serviceType
        );

        return basicServiceValidation;
      } else {
        // Product validation - productType is no longer required
        const basicValidation = (
          formData.value.name &&
          formData.value.description &&
          formData.value.description.length <= 2048 &&
          formData.value.price !== null
        );

        return basicValidation;
      }
    });

    // Function to check if all form fields are empty
    const areAllFieldsEmpty = () => {
      const physicalFieldsEmpty = formData.value.productType !== 'Físico' || (
        (formData.value.weightKg === null || formData.value.weightKg === 0) &&
        (formData.value.dimensionsCm.height === null || formData.value.dimensionsCm.height === 0) &&
        (formData.value.dimensionsCm.width === null || formData.value.dimensionsCm.width === 0) &&
        (formData.value.dimensionsCm.depth === null || formData.value.dimensionsCm.depth === 0) &&
        formData.value.color.trim() === '' &&
        formData.value.shipsTo.trim() === ''
      );

      const digitalFieldsEmpty = formData.value.productType !== 'Digital' || (
        formData.value.fileFormat.trim() === '' &&
        formData.value.licenseType.trim() === ''
      );

      return (
        formData.value.name.trim() === '' &&
        formData.value.description.trim() === '' &&
        (formData.value.price === null || formData.value.price === 0) &&
        (formData.value.discountPrice === null || formData.value.discountPrice === 0) &&
        formData.value.contextUrl.trim() === '' &&
        formData.value.productType === '' &&
        formData.value.selectedInboxes.length === 0 &&
        formData.value.category.length === 0 &&
        formData.value.images.length === 0 &&
        physicalFieldsEmpty &&
        digitalFieldsEmpty
      );
    };

    // Function to handle backdrop click
    const handleBackdropClick = () => {
      // Only close if all fields are empty
      if (areAllFieldsEmpty()) {
        closeModal();
      }
      // Otherwise, do nothing - prevents modal from closing
    };

    const closeModal = () => {
      emit('close');
    };

    // Format service type for display
    const formatServiceType = (type) => {
      const typeMap = {
        'aluguel': 'Aluguel',
        'delivery': 'Delivery',
        'consulta': 'Consulta',
        'tratamento': 'Tratamento',
        'assinatura': 'Assinatura'
      };
      return typeMap[type] || type;
    };

    function getCookie(name) {
      const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
      return match ? decodeURIComponent(match[2]) : null;
    }

    function getAuthHeaders() {
      const cookie = getCookie('cw_d_session_info');
      if (!cookie) return {};
      try {
        const session = JSON.parse(cookie);
        return {
          'access-token': session['access-token'],
          client: session.client,
          uid: session.uid
        };
      } catch {
        return {};
      }
    }

    const uploadTemporaryImages = async (images) => {
      const headers = getAuthHeaders();
      if (!headers['access-token']) {
        throw new Error('Usuário não autenticado.');
      }

      const uploadPromises = images.map(async (image) => {
        if (!image.isTemporary) {
          // Already uploaded image, return as-is
          const urlString = typeof image === 'string' ? image : image.url;

          // Rejeita imagens em formato base64
          if (urlString?.startsWith('data:')) {
            throw new Error('Formato inválido. Por favor, utilize outro formato.');
          }

          return urlString;
        }

        // Upload temporary image to Firebase
        const formData = new FormData();
        formData.append('file', image.file);

        try {
          const response = await wootAPI.post('/api/v1/catalogs/upload_image', formData, {
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          });

          return response.data.url;
        } catch (err) {
          // Check for specific file size error from server
          if (err.response?.data?.error === 'Arquivo muito grande. Máximo 5MB permitido') {
            throw new Error('Arquivo muito grande. Máximo 5MB permitido');
          }
          throw err;
        }
      });

      return await Promise.all(uploadPromises);
    };

    const triggerFileUpload = () => {
      if (fileInput.value) {
        fileInput.value.click();
      }
    };

    const handleFileUpload = async (event) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      loading.value = true;
      error.value = null;

      try {
        const filePromises = Array.from(files).map(async (file) => {
          if (!file.type.startsWith('image/')) {
            console.warn('Arquivo ignorado (não é imagem):', file.name);
            return null;
          }
          
          // Check file size (5MB = 5 * 1024 * 1024 bytes)
          const maxSizeInBytes = 5 * 1024 * 1024;
          if (file.size > maxSizeInBytes) {
            throw new Error('Arquivo muito grande. Máximo 5MB permitido');
          }
          
          // Store file temporarily as base64 for preview
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: Date.now().toString() + Math.random().toString(36),
                url: e.target.result, // base64 data URL for preview
                file: file, // store original file for later upload
                isTemporary: true // flag to identify temporary images
              });
            };
            reader.readAsDataURL(file);
          });
        });

        const tempImages = (await Promise.all(filePromises)).filter(Boolean);
        
        // Add temporary images to form data
        formData.value.images.push(...tempImages);

      } catch (err) {
        console.error('Erro ao processar imagens:', err);
        error.value = err.message || 'Erro ao processar as imagens.';
      } finally {
        loading.value = false;
        // Clear the file input so the same file can be selected again
        if (fileInput.value) {
          fileInput.value.value = '';
        }
      }
    };

    const saveProduct = debounce(async () => {
      if (!isFormValid.value) return;

      loading.value = true;
      error.value = null;

      // Get auth headers and verify user is logged in
      const headers = getAuthHeaders();
      if (!headers['access-token']) {
        error.value = 'Usuário não autenticado.';
        loading.value = false;
        return;
      }

      const categoryNames = formData.value.category.map(item =>
        typeof item === 'string' ? item : item.name
      );

      // Upload temporary images to Firebase before saving
      const imageUrls = await uploadTemporaryImages(formData.value.images);

      if (isService.value) {
        // Handle service update
        const updatedService = {
          name: formData.value.name,
          description: formData.value.description,
          price: formData.value.price,
          discountPrice: formData.value.discountPrice,
          discountReason: formData.value.discountReason,
          contextUrl: formData.value.link,
          selectedInboxes: formData.value.selectedInboxes.includes('todos') ? null : formData.value.selectedInboxes,
          product_type: formData.value.serviceType,
          category: categoryNames,
          images: imageUrls,
          address: formData.value.address,
          // Campos específicos para delivery
          ...(formData.value.serviceType === 'delivery' && {
            maxDistance: formData.value.maxDistance,
            isFixedPrice: formData.value.isFixedPrice,
            estimatedMinutes: formData.value.estimatedMinutes
          }),
          // Campos específicos para consulta, tratamento e aluguel
          ...((['consulta', 'tratamento', 'aluguel'].includes(formData.value.serviceType)) && {
            isHourlyRate: formData.value.isHourlyRate,
            advanceBookingDays: formData.value.advanceBookingDays
          }),
          // Specialty for consulta and tratamento
          ...((['consulta', 'tratamento'].includes(formData.value.serviceType)) && {
            specialty: formData.value.specialty
          }),
          // Duration for consulta and tratamento
          ...((['consulta', 'tratamento'].includes(formData.value.serviceType)) && {
            durationMinutes: formData.value.durationMinutes
          }),
          // Campos específicos para assinatura
          ...(formData.value.serviceType === 'assinatura' && {
            planType: formData.value.planType
          })
        };

        try {
          const response = await wootAPI.patch(
            `/api/v1/catalogs/${props.product.id}`,
            { product: updatedService },
            { headers }
          );

          const savedService = response.data;
          emit('update', savedService);
          emit('success', savedService);
          closeModal();
        } catch (err) {
          console.error('Erro ao editar serviço', err);
          // Check for specific file size error
          if (err.message === 'Arquivo muito grande. Máximo 5MB permitido') {
            error.value = 'Arquivo muito grande. Máximo 5MB permitido';
          } else {
            error.value = err.response?.data?.message ||
              'Erro interno do servidor. Tente novamente.';
          }
        } finally {
          loading.value = false;
        }
      } else {
        // Handle product update
        // Prepare dimensions array for physical products
        let dimensionsArray = null;
        if (formData.value.productType === 'Físico') {
          const { height, width, depth } = formData.value.dimensionsCm;
          if (height || width || depth) {
            dimensionsArray = [height || 0, width || 0, depth || 0];
          }
        }

        // Prepare updated product data
        const updatedProduct = {
          name: formData.value.name,
          description: formData.value.description,
          price: formData.value.price,
          discountPrice: formData.value.discountPrice,
          discountReason: formData.value.discountReason,
          contextUrl: formData.value.contextUrl,
          selectedInboxes: formData.value.selectedInboxes.includes('todos') ? null : formData.value.selectedInboxes,
          category: categoryNames,
          images: imageUrls,
          product_type: formData.value.productType === 'Físico' ? 'physical' : 'digital',
          // Campos específicos para produtos físicos
          ...(formData.value.productType === 'Físico' && {
            weightKg: formData.value.weightKg,
            dimensionsCm: dimensionsArray,
            color: formData.value.color,
            shipsTo: formData.value.shipsTo
          }),
          // Campos específicos para produtos digitais
          ...(formData.value.productType === 'Digital' && {
            fileFormat: formData.value.fileFormat,
            licenseType: formData.value.licenseType
          })
        };

        try {
          const response = await wootAPI.patch(
            `/api/v1/catalogs/${props.product.id}`,
            { product: updatedProduct },
            { headers }
          );

          const savedProduct = response.data;
          emit('update', savedProduct);
          emit('success', savedProduct);
          closeModal();
        } catch (err) {
          console.error('Erro ao editar produto', err);
          // Check for specific file size error
          if (err.message === 'Arquivo muito grande. Máximo 5MB permitido') {
            error.value = 'Arquivo muito grande. Máximo 5MB permitido';
          } else {
            error.value = err.response?.data?.message ||
              'Erro interno do servidor. Tente novamente.';
          }
        } finally {
          loading.value = false;
        }
      }
    }, 300);

    // Inbox functionality (copied from CatalogModal)
    const groupedInboxOptions = computed(() => {
      const result = [];
      
      // Get all the inbox options
      const inboxes = inboxOptions.value || [];
      
      // Extract channel types for grouping
      const whatsappEvolution = [];
      const whatsappOfficial = [];
      const otherChannels = [];
      
      inboxes.forEach(inbox => {
        // Use name to identify the type since integration_type might not be available
        const name = (inbox.inbox_name || inbox.name || '').toLowerCase();
        
        // Official API inboxes will have cloud_api or official in the name or a provider property
        if (inbox.integration_type === 'official' || 
            name.includes('api oficial') ||
            name.includes('cloud') ||
            name.includes('official')) {
          whatsappOfficial.push({...inbox, integration_type: 'official'});
        }
        // Evolution inboxes will have evolution in the name
        else if (inbox.integration_type === 'evolution' || name.includes('evolution') || name.includes('unofficial')) {
          whatsappEvolution.push({...inbox, integration_type: 'evolution'});
        }
        // Otherwise, put it in other channels
        else {
          otherChannels.push(inbox);
        }
      });
      
      // Add evolution group
      if (whatsappEvolution.length > 0) {
        result.push({ type: 'header', label: 'WHATSAPP NÃO OFICIAL' });
        result.push(...whatsappEvolution);
      }
      
      // Add official group
      if (whatsappOfficial.length > 0) {
        result.push({ type: 'header', label: 'WHATSAPP API OFICIAL' });
        result.push(...whatsappOfficial);
      }
      
      // Add other channels group
      if (otherChannels.length > 0) {
        result.push({ type: 'header', label: 'OUTROS CANAIS' });
        result.push(...otherChannels);
      }
      
      return result;
    });
    
    // Helper function to get the appropriate icon for an inbox type
    const getInboxTypeIcon = (type) => {
      switch (type?.toLowerCase()) {
        case 'whatsapp':
          return 'i-lucide-message-circle';
        case 'facebook':
          return 'i-lucide-facebook';
        case 'twitter':
          return 'i-lucide-twitter';
        case 'email':
          return 'i-lucide-mail';
        case 'web':
          return 'i-lucide-globe';
        case 'telegram':
          return 'i-lucide-send';
        default:
          return 'i-lucide-message-square';
      }
    };
    
    // Toggle dropdown
    const toggleInboxDropdown = () => {
      showInboxDropdown.value = !showInboxDropdown.value;
    };
    
    // Use connected inboxes passed from parent component
    const loadInboxes = () => {
      try {
        // Use the connected inboxes passed as prop - no transformation needed
        // as the data is already in the correct format from load_inboxes endpoint
        const connectedInboxes = props.connectedInboxes || [];
        
        console.log('Using connected inboxes from parent:', connectedInboxes.length);
        inboxOptions.value = connectedInboxes;
        
        // Now restore selection with proper type normalization
        const validSelectedInboxes = originalSelectedInboxes.value
          .filter(id => {
            return id === 'todos'
              ? true
              : connectedInboxes.some(inbox => String(inbox.inbox_id) === id);
          });

        // Only default to 'todos' if the original selection wasn't explicitly empty
        const wasExplicitlyEmpty = Array.isArray(props.product.inbox_id) && props.product.inbox_id.length === 0;
        formData.value.selectedInboxes = validSelectedInboxes.length
          ? validSelectedInboxes
          : wasExplicitlyEmpty ? [] : ['todos'];
        
        console.log('Restored selected inboxes:', formData.value.selectedInboxes);
      } catch (err) {
        console.error('Falha ao carregar inboxes:', err);
        // Don't show error to user as this is optional
        inboxOptions.value = [];
      }
    };
    
    // Load specialties
    const loadSpecialties = async () => {
      try {
        const headers = getAuthHeaders();
        const response = await wootAPI.get('/api/v1/specialties?per_page=100', { headers });
        specialties.value = response.data.data;
      } catch (err) {
        console.error('Falha ao carregar especialidades:', err);
        // Don't show error to user as this is optional
        specialties.value = [];
      }
    };
    
    // Multi-select inbox functionality
    const isInboxSelected = (inboxId) => {
      return formData.value.selectedInboxes.includes(inboxId);
    };
    
    const toggleInboxSelection = (inboxId) => {
      const currentSelection = [...formData.value.selectedInboxes];
      
      if (inboxId === 'todos') {
        // If selecting "Todos", clear all individual selections
        if (currentSelection.includes('todos')) {
          // Don't allow deselecting if it's the only selection
          if (currentSelection.length === 1) {
            // Show error or just return without changing
            error.value = 'Deve selecionar pelo menos um inbox';
            return;
          }
          // If "Todos" is already selected, deselect it
          formData.value.selectedInboxes = [];
        } else {
          // Select "Todos" and clear individual selections
          formData.value.selectedInboxes = ['todos'];
        }
      } else {
        // Individual inbox selection
        const todosIndex = currentSelection.indexOf('todos');
        const inboxIndex = currentSelection.indexOf(inboxId);
        
        // Remove "Todos" if it was selected (since we're selecting specific inboxes)
        if (todosIndex !== -1) {
          currentSelection.splice(todosIndex, 1);
        }
        
        if (inboxIndex === -1) {
          // Add inbox to selection
          currentSelection.push(inboxId);
        } else {
          // Don't allow removing if it's the last selection
          if (currentSelection.length === 1) {
            error.value = 'Deve selecionar pelo menos um inbox';
            return;
          }
          // Remove inbox from selection
          currentSelection.splice(inboxIndex, 1);
        }
        
        formData.value.selectedInboxes = currentSelection;
      }
      
      // Clear error if selection is valid
      if (formData.value.selectedInboxes.length > 0) {
        error.value = null;
      }
    };
    
    const getInboxName = (inboxId) => {
      if (inboxId === 'todos') return 'Todos';
      
      // First try to get display_name from store
      const displayName = store.getters['inboxMetadata/getInboxDisplayName'](inboxId);
      if (displayName) return displayName;
      
      // Fallback to inbox_name from inboxOptions
      const inbox = inboxOptions.value.find(inbox => 
        String(inbox.inbox_id) === inboxId
      );
      
      return inbox ? inbox.inbox_name : 'Inbox desconhecido';
    };
    
    const getInboxDisplayName = (inbox) => {
      // First try to get display_name from store
      const displayName = store.getters['inboxMetadata/getInboxDisplayName'](inbox.inbox_id || inbox.id);
      if (displayName) return displayName;
      
      // Fallback to inbox_name or name
      return inbox.inbox_name || inbox.name || `Inbox ${inbox.inbox_id || inbox.id}`;
    };
    
    const getSelectedInboxesDisplayText = () => {
      const selected = formData.value.selectedInboxes;
      
      if (selected.includes('todos')) {
        return 'Todos';
      } else if (selected.length === 0) {
        return 'Selecionar inboxes';
      } else if (selected.length === 1) {
        return getInboxName(selected[0]);
      } else {
        return `${selected.length} inboxes selecionados`;
      }
    };
    
    const getSelectedInboxesDisplayIcon = () => {
      const selected = formData.value.selectedInboxes;
      
      if (selected.includes('todos')) {
        return 'i-lucide-globe';
      } else if (selected.length === 0) {
        return 'i-lucide-inbox';
      } else if (selected.length === 1) {
        const inbox = inboxOptions.value.find(inbox => 
          String(inbox.inbox_id) === selected[0]
        );
        return inbox ? getInboxTypeIcon(inbox.channel_type) : 'i-lucide-inbox';
      } else {
        return 'i-lucide-layers';
      }
    };
    
    const getSelectedInboxesDisplayClasses = () => {
      const selected = formData.value.selectedInboxes;
      
      if (selected.length === 0) {
        return 'bg-n-slate-2 text-n-slate-11';
      } else if (selected.includes('todos')) {
        return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
      } else {
        return 'bg-grass-300 text-grass-900';
      }
    };

    return {
      isService,
      formData,
      formattedValue,
      formattedDiscountPrice,
      handleValueInput,
      handleDiscountPriceInput,
      handleDiscountPriceKeydown,
      isFormValid,
      closeModal,
      saveProduct,
      loading,
      error,
      handleBackdropClick,
      areAllFieldsEmpty,
      descriptionCharsRemaining,
      formatServiceType,
      // File upload functionality
      fileInput,
      triggerFileUpload,
      handleFileUpload,
      uploadTemporaryImages,
      // Inbox functionality
      inboxOptions,
      showInboxDropdown,
      groupedInboxOptions,
      getInboxTypeIcon,
      toggleInboxDropdown,
      // Multi-select inbox functionality
      isInboxSelected,
      toggleInboxSelection,
      getInboxName,
      getInboxDisplayName,
      getSelectedInboxesDisplayText,
      getSelectedInboxesDisplayIcon,
      getSelectedInboxesDisplayClasses,
      // Specialties functionality
      specialties
    };
  }
};
</script>

<style scoped>
:root {
  --light-primary-hover: #dddddd;
  --transition-speed: 0.2s;
}

/* Modal styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-container {
  border-radius: 8px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid rgb(var(--slate-4) / 1);
}

.dark .modal-header {
  border-bottom: 1px solid rgb(var(--slate-6) / 1);
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid rgb(var(--slate-4) / 1);
}

.dark .modal-footer {
  border-top: 1px solid rgb(var(--slate-6) / 1);
}

/* Form styles */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgb(var(--slate-4) / 1);
  border-radius: 8px;
  font-size: 14px;
}

.dark .form-input {
  border-color: rgb(var(--slate-7) / 1);
}

/* Input group styling for the product value field */
.input-group {
  display: flex;
  align-items: start;
}

.input-prefix {
  padding: 10px 12px;
  background: rgb(var(--slate-4) / 1);
  border: 1px solid rgb(var(--slate-4) / 1);
  border-right: none;
  border-radius: 8px 0 0 8px;
  font-size: 14px;
}

.dark .input-prefix {
  background: rgb(var(--slate-7) / 1);
  border-color: rgb(var(--slate-7) / 1);
}

.number-input {
  border-radius: 0 8px 8px 0 !important;
  margin-top: 1px !important;
}

/* Responsive layout */
@media (max-width: 768px) {
  .modal-body .flex {
    flex-direction: column;
  }
}

/* Chrome, Safari, Edge (WebKit/Blink) */
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}
</style>